import { HTMLAttributes, useState } from "react";
import Card from "../../formatting/Card";
import { useContract, useRole } from "../ContractContext";
import styled from "styled-components";
import { ContractField, useContractField } from "../fields/ContractFieldInput";
import InputDescription from "../../forms/InputDescription";
import { onDesktop } from "../../shared/layout/Breakpoints";
import { Role } from "../model/ContractViewModel";
import ContractCheckboxField from "../fields/form-controls/ContractCheckboxField";
import { ExpandChevron } from "@teamscheme/icons";
import {
  AdditionalFieldDescription,
  NationalIdentityNumberInputField,
} from "./CustomContactInfoInputFields";

const CollapsedSectionContent = styled.div`
  margin-top: 1em;
`;

const IconWrapper = styled.div`
  display: flex;
  height: ${({ theme }) => theme.spacing.space24};
`;

const AccordionButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  border: none;
  background: none;
`;

const CollapsedSection = styled(function ({
  title,
  children,
  open,
  description,
  ...props
}: {
  title: string;
  open?: boolean;
  description: string;
} & HTMLAttributes<HTMLDivElement>) {
  const [isOpen, setIsOpen] = useState(open || false);
  return (
    <div {...props}>
      <AccordionButton
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen((x) => !x);
        }}
      >
        <h3>
          <span>{title}</span>
          <IconWrapper>
            <ExpandChevron open={isOpen} black />
          </IconWrapper>
        </h3>
      </AccordionButton>
      {isOpen ? (
        <CollapsedSectionContent>
          <InputDescription description={description} />
          <div>{children}</div>
        </CollapsedSectionContent>
      ) : null}
    </div>
  );
})`
  h3 {
    cursor: pointer;
    display: flex;
    align-items: center;
    margin-bottom: 0;
    padding: ${({ theme }) => theme.spacing.space24} 0;

    & > span {
      flex: 1 1 auto;
      display: flex;
      align-items: center;
    }

    ${ExpandChevron} {
      height: ${({ theme }) => theme.spacing.space24};
      width: ${({ theme }) => theme.spacing.space24};
    }
  }
`;

export const CoOwnerCard = styled(function (
  props: Omit<HTMLAttributes<HTMLDivElement>, "children">
) {
  const role = useRole();
  const contract = useContract();
  const section = role === Role.Seller ? "sellerCoOwner" : "buyerCoOwner";
  const coOwner =
    role === Role.Seller ? contract.sellerCoOwner : contract.buyerCoOwner;
  const nationalIdentityNumberValue = useContractField<string>({
    section,
    name: "nationalIdentityNumber",
    type: "tel",
  });

  const includedLabel =
    role === Role.Seller
      ? "Kjøretøyet har registrert medeier"
      : "Kjøretøyet skal ha registrert medeieer";

  const description =
    role === Role.Seller
      ? "Hvis kjøretøyet er registrert med en medeier må denne personen også se over kontrakten og signere. Medeier vil få en invitasjon til å signere etter at du har signert."
      : "Hvis kjøretøyet skal registreres med medeier må denne personen også se over kontrakten og signere. Medeier vil få en invitasjon til å signere etter at du har signert.";

  return (
    <Card {...props}>
      <CollapsedSection
        title="Legg til opplysninger om medeier"
        description={description}
        open={coOwner?.included}
      >
        <div className="ContactInfoForm">
          <ContractCheckboxField
            section={section}
            name="included"
            label={includedLabel}
          />
          <ContractField
            disabled={!coOwner?.included}
            section={section}
            name="phone"
            type="phone"
          />
          <ContractField
            disabled={!coOwner?.included}
            section={section}
            name="firstName"
            type="text"
          />
          <ContractField
            disabled={!coOwner?.included}
            section={section}
            name="lastName"
            type="text"
          />
          {contract.ownershipTransfer?.featureEnabled ? (
            <>
              <NationalIdentityNumberInputField
                section={section}
                inputProps={{
                  autoComplete: "off",
                  disabled: !coOwner?.included,
                }}
                value={nationalIdentityNumberValue}
              />

              <AdditionalFieldDescription>
                Fyll ut hvis du ønsker eierskiftet registrert hos Statens
                vegvesen med NAF Kjøpekontrakt.
              </AdditionalFieldDescription>
            </>
          ) : null}
          <ContractField
            disabled={!coOwner?.included}
            section={section}
            name="email"
            type="email"
          />
        </div>
      </CollapsedSection>
    </Card>
  );
})`
  margin-left: -0.5em;
  margin-right: -0.5em;
  padding: 0 0.5em;

  ${AdditionalFieldDescription} {
    padding: 0;
  }

  ${onDesktop`
    margin-left: -1em;
    margin-right: -1em;
    padding-left: 1em;
    padding-right: 1em;
  `}
`;
